import { useImportHistory } from '@/hooks/useImportHistory';
import { useImportMutations } from '@/hooks/useImportMutations';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

export function ImportHistory() {
  const { data: history, isLoading, isError, error } = useImportHistory();
  const { undoImport, isUndoing } = useImportMutations();

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'secondary';
      case 'failed':
      case 'revert_failed':
        return 'destructive';
      case 'reverted':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-destructive">
        <AlertCircle className="h-8 w-8 mb-2" />
        <p>Error loading import history:</p>
        <p className="text-sm">{error?.message}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Import History</h2>
      <p className="text-muted-foreground mb-6">Review your past imports. You can undo completed imports to remove all associated contacts.</p>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Contacts Imported</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history && history.length > 0 ? (
              history.map((batch) => (
                <TableRow key={batch.id}>
                  <TableCell>{format(new Date(batch.created_at), 'PPp')}</TableCell>
                  <TableCell className="font-medium">{batch.file_name}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(batch.status)}>{batch.status}</Badge>
                  </TableCell>
                  <TableCell>{batch.contacts_imported}</TableCell>
                  <TableCell className="text-right">
                    {(batch.status === 'completed' || batch.status === 'failed' || batch.status === 'processing') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => undoImport(batch.id)}
                        disabled={isUndoing}
                      >
                        Undo
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No import history found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
